import { DataTable, DataTableColumn } from "mantine-datatable"
import { ABSENCE_TYPE, ATTENDANCE_MODE, ATTENDANCE_STATUS, PAGE_SIZES } from "../../../../constants/status"
import { useState } from "react"; // Loại bỏ useEffect
import ImageCustom from "../../../../components/custom/image-custom";
import { formatCurrencyDecimal } from "../../../../utils/helpers";
import { LabelStatusCustom } from "../../../../components/custom/label-status-custom";
import Tippy from "@tippyjs/react";
import { IconEye } from "../../../../components/icons/icon-group";
import { parse, isBefore, format, startOfDay } from "date-fns";
import { vi } from "date-fns/locale";

interface Props {
    dataTable: any[];
    loadingTable: boolean;
    page: number;
    limit: number;
    totalRecords: number;
    setPage: (page: number) => void;
    setLimit: (limit: number) => void;
    onShowModal: (item: any) => void;
}

const AttendanceManagementTable = ({
    dataTable,
    loadingTable,
    page,
    limit,
    totalRecords,
    setPage,
    setLimit,
    onShowModal,
}: Props) => {

    const renderCheckLabel = (dateTime: string, timeString: string, label: string) => {
        const now = new Date();

        const date = dateTime
            ? parse(dateTime, "dd/MM/yyyy", new Date(), { locale: vi })
            : null;

        const time = timeString
            ? parse(timeString, "dd/MM/yyyy HH:mm", new Date(), { locale: vi })
            : null;

        let content = "";
        let backgroundColor = "";
        let textColor = "";

        if (time && !isNaN(time.getTime())) {
            // Có giờ check in/out
            content = `${format(time, "HH:mm")}`;
            backgroundColor = "bg-green-100";
            textColor = "text-green-700";
        } else if (date && isBefore(startOfDay(date), startOfDay(now))) {
            // Ngày đã qua nhưng không có giờ check
            content = `Quên ${label.toLowerCase()}`;
            backgroundColor = "bg-red-100";
            textColor = "text-red-700";
        } else {
            // Ngày hôm nay hoặc tương lai, chưa check
            content = `Chưa ${label.toLowerCase()}`;
            backgroundColor = "bg-orange-100";
            textColor = "text-orange-700";
        }

        return (
            <LabelStatusCustom
                content={content}
                backgroundColor={backgroundColor}
                textColor={textColor}
                className="min-w-28"
            />
        );
    };

    const attendanceStatusMap: Record<number, { content: string; backgroundColor: string; textColor: string }> = {
        [ATTENDANCE_STATUS.NONE]: { content: "Không có", backgroundColor: "bg-gray-100", textColor: "text-gray-700" },
        [ATTENDANCE_STATUS.ON_TIME]: { content: "Đúng giờ", backgroundColor: "bg-green-100", textColor: "text-green-700" },
        [ATTENDANCE_STATUS.LATE]: { content: "Trễ", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
        [ATTENDANCE_STATUS.LEAVE_EARLY]: { content: "Về sớm", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
        [ATTENDANCE_STATUS.NOT_CHECKED_IN]: { content: "Chưa check in", backgroundColor: "bg-red-100", textColor: "text-red-700" },
        [ATTENDANCE_STATUS.NOT_CHECKED_OUT]: { content: "Chưa check out", backgroundColor: "bg-red-100", textColor: "text-red-700" },
    };


    const columnEffect: DataTableColumn<any>[] = [
        // ... giữ nguyên cột ...
        {
            accessor: "employee.name", width: 230, title: "Tên nhân viên", textAlign: 'center',
            render: ({ employee }) => (
                <div className="flex items-center gap-2" key={`employee-${employee.id ?? 'unknown'}`}>
                    <div className="border rounded-full">
                        <ImageCustom url={employee.avatar} className="w-10 h-10 rounded-full" />
                    </div>
                    <div className="flex flex-col flex-1 text-left break-words whitespace-normal">
                        <span className="text-base">{employee.name}</span>
                        <span className="text-sm text-gray-500">{employee.department_name}</span>
                    </div>
                </div>
            )
        },
        {
            accessor: "check_time_work", textAlign: "center", width: 280,
            title: (
                <div className="flex flex-col">
                    <span>Thời gian</span>
                    <span className="text-xs font-normal">(Check in - Check out)</span>
                </div>
            ),
            render: ({ check_in_at, check_out_at, date }) => (
                <div className="flex items-center justify-center gap-2">
                    {renderCheckLabel(date, check_in_at, "Check in")} - {renderCheckLabel(date, check_out_at, "Check out")}
                </div>
            )
        },
        {
            accessor: "minutes_late", title: "Đi trễ (phút)", textAlign: "center",
            render: ({ minutes_late, attendance_mode }) => (
                attendance_mode === ATTENDANCE_MODE.PART_TIME ? <span>-</span> : <span>{formatCurrencyDecimal(minutes_late)}</span>
            )
        },
        {
            accessor: "minutes_early", title: "Về sớm (phút)", textAlign: "center",
            render: ({ minutes_early, attendance_mode }) => (
                attendance_mode === ATTENDANCE_MODE.PART_TIME ? <span>-</span> : <span>{formatCurrencyDecimal(minutes_early)}</span>
            )
        },
        {
            accessor: "shift", title: "Ca làm việc", textAlign: "center", width: 230,
            render: ({ shift, work_schedule, attendance_mode }) => {
                // NO_ATTENDANCE (quản lý/tài xế/kiểm đếm) — không có khái niệm ca/lịch làm việc.
                if (attendance_mode === ATTENDANCE_MODE.NO_ATTENDANCE) {
                    return <span className="text-gray-400">Không chấm công</span>;
                }

                // NV Part-time — linh hoạt trong khung giờ 05:00 - 22:00
                if (attendance_mode === ATTENDANCE_MODE.PART_TIME) {
                    return (
                        <div className='flex items-stretch justify-center w-full p-2 text-left text-purple-600 border border-purple-500 rounded-md bg-purple-500/10'>
                            <span className="flex-shrink-0 w-1 bg-purple-500 rounded-full" />
                            <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                                <span className="text-base font-bold">05:00 - 22:00</span>
                                <span className="text-sm">Partime</span>
                            </div>
                        </div>
                    );
                }

                // NV theo ca (SHIFT) — có ca hôm nay.
                if (shift?.id && shift.id !== '0') {
                    return (
                        <div className='flex items-stretch justify-center w-full p-2 text-left text-blue-500 border border-blue-500 rounded-md bg-blue-500/10'>
                            <span className="flex-shrink-0 w-1 bg-blue-500 rounded-full" />
                            <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                                <span className="text-base font-bold">
                                    {shift.start_time}
                                    {' - '}
                                    {shift.end_time}
                                </span>
                                <span className="text-sm">{shift.name}</span>
                            </div>
                        </div>
                    );
                }

                // NV giờ hành chính (FIXED) — có cấu hình lịch cố định (đi làm hoặc nghỉ tuần hôm nay).
                if (work_schedule) {
                    if (!work_schedule.is_working) {
                        return (
                            <div className="flex items-stretch justify-center w-full p-2 text-left text-gray-500 border border-gray-300 rounded-md bg-gray-100">
                                <span className="flex-shrink-0 w-1 bg-gray-400 rounded-full" />
                                <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                                    <span className="text-sm font-semibold">Nghỉ theo lịch</span>
                                    <span className="text-sm">{work_schedule.name}</span>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className='flex items-stretch justify-center w-full p-2 text-left text-teal-600 border border-teal-500 rounded-md bg-teal-500/10'>
                            <span className="flex-shrink-0 w-1 bg-teal-500 rounded-full" />
                            <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                                <span className="text-base font-bold">
                                    {work_schedule.start_time}
                                    {' - '}
                                    {work_schedule.end_time}
                                </span>
                                <span className="text-sm">{work_schedule.name}</span>
                            </div>
                        </div>
                    );
                }

                // SHIFT chưa được xếp ca hôm nay, hoặc FIXED chưa cấu hình lịch (kể cả DEFAULT).
                return "-";
            }
        },
        {
            accessor: "check_in_status",
            title: <span>Trạng thái<br />check in</span>,
            textAlign: "center",
            width: 180,
            render: ({ check_in_status, attendance_mode, check_in_at }) => {
                // NO_ATTENDANCE không bao giờ check-in được (chặn ở BE) — "Chưa check in" gây hiểu
                // nhầm là thiếu sót, hiện trung tính thay vì đỏ.
                if (attendance_mode === ATTENDANCE_MODE.NO_ATTENDANCE) {
                    return <LabelStatusCustom content="Không chấm công" backgroundColor="bg-gray-100" textColor="text-gray-500" />;
                }
                if (attendance_mode === ATTENDANCE_MODE.PART_TIME) {
                    if (!check_in_at) {
                        return <LabelStatusCustom content="Chưa check in" backgroundColor="bg-gray-100" textColor="text-gray-500" />;
                    }
                    return <LabelStatusCustom content="Đúng giờ" backgroundColor="bg-green-100" textColor="text-green-700" />;
                }
                const data = attendanceStatusMap[check_in_status] ?? attendanceStatusMap[ATTENDANCE_STATUS.NONE];
                return (
                    <LabelStatusCustom
                        content={data.content}
                        backgroundColor={data.backgroundColor}
                        textColor={data.textColor}
                    />
                );
            },
        },
        {
            accessor: "check_out_status",
            title: <span>Trạng thái<br />check out</span>,
            textAlign: "center",
            width: 180,
            render: ({ check_out_status, attendance_mode, check_out_at, check_in_at }) => {
                if (attendance_mode === ATTENDANCE_MODE.NO_ATTENDANCE) {
                    return <LabelStatusCustom content="Không chấm công" backgroundColor="bg-gray-100" textColor="text-gray-500" />;
                }
                if (attendance_mode === ATTENDANCE_MODE.PART_TIME) {
                    if (!check_in_at) {
                        return <span className="text-gray-400">-</span>;
                    }
                    if (!check_out_at) {
                        return <LabelStatusCustom content="Chưa check out" backgroundColor="bg-yellow-100" textColor="text-yellow-700" />;
                    }
                    return <LabelStatusCustom content="Đúng giờ" backgroundColor="bg-green-100" textColor="text-green-700" />;
                }
                const data = attendanceStatusMap[check_out_status] ?? attendanceStatusMap[ATTENDANCE_STATUS.NONE];
                return (
                    <LabelStatusCustom
                        content={data.content}
                        backgroundColor={data.backgroundColor}
                        textColor={data.textColor}
                    />
                );
            },
        },
        {
            accessor: "address_check_in",
            title: "Vị trí check in",
            textAlign: "center",
            width: 300,
            render: ({ address_check_in }) => (
                address_check_in ? (
                    <span className="whitespace-normal break-works">{address_check_in}</span>
                ) : (
                    <span className="text-gray-400">-</span>
                )
            )
        },
        {
            accessor: "address_check_out",
            title: "Vị trí check out",
            textAlign: "center",
            width: 300,
            render: ({ address_check_out }) => (
                address_check_out ? (
                    <span className="whitespace-normal break-works">{address_check_out}</span>
                ) : (
                    <span className="text-gray-400">-</span>
                )
            )
        },
        {
            accessor: "avatar_check_in", title: "Ảnh check in", textAlign: "center", width: 140,
            render: ({ avatar_check_in, check_in_status }) => (
                check_in_status === ATTENDANCE_STATUS.NOT_CHECKED_IN ? (
                    <span className="text-gray-400">-</span>
                ) : (
                    <div className="m-auto w-max">
                        <ImageCustom
                            isReview
                            isUser={false}
                            url={avatar_check_in}
                            isLocal={false}
                            className="w-10 h-10 rounded-lg"
                        />
                    </div>
                )
            )
        },
        {
            accessor: "avatar_check_out", title: "Ảnh check out", textAlign: "center", width: 140,
            render: ({ avatar_check_out, check_out_status }) => (
                check_out_status === ATTENDANCE_STATUS.NOT_CHECKED_OUT ? (
                    <span className="text-gray-400">-</span>
                ) : (
                    <div className="m-auto w-max">
                        <ImageCustom
                            isReview
                            isUser={false}
                            url={avatar_check_out}
                            isLocal={false}
                            className="w-10 h-10 rounded-lg"
                        />
                    </div>
                )
            )
        },
        {
            accessor: "absence_type", title: "Loại vắng mặt", textAlign: "center", width: 150,
            render: ({ absence_type }) => {
                const absenceTypeMap: Record<number, { content: string; backgroundColor: string; textColor: string }> = {
                    [ABSENCE_TYPE.NONE]: { content: "Không vắng", backgroundColor: "bg-green-100", textColor: "text-green-700" },
                    [ABSENCE_TYPE.PERMITTED_PAID]: { content: "Có phép - có lương", backgroundColor: "bg-blue-100", textColor: "text-blue-700" },
                    [ABSENCE_TYPE.PERMITTED_UNPAID]: { content: "Có phép - không lương", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
                    [ABSENCE_TYPE.UNPERMITTED]: { content: "Không phép", backgroundColor: "bg-red-100", textColor: "text-red-700" },
                    [ABSENCE_TYPE.NO_SHIFT]: { content: "Không có ca làm", backgroundColor: "bg-gray-100", textColor: "text-gray-700" },
                };

                return (
                    <LabelStatusCustom
                        content={absenceTypeMap[absence_type]?.content ?? "Không xác định"}
                        backgroundColor={absenceTypeMap[absence_type]?.backgroundColor ?? "bg-gray-100"}
                        textColor={absenceTypeMap[absence_type]?.textColor ?? "text-gray-700"}
                    />
                );
            },
        },
        {
            accessor: "action", title: "Hành động", textAlign: "center",
            render: ({ employee_id }) => {
                const selectedItem = dataTable.find((item) => item.employee_id === employee_id) ?? {};
                return (
                    <div className="flex items-center gap-2 mx-auto w-max">

                        <Tippy content="Chi tiết">
                            <button type="button" onClick={() => onShowModal(selectedItem)}>
                                <IconEye />
                            </button>
                        </Tippy>
                    </div>
                )
            }
        },
    ]

    return (
        <div className="datatables min-h-52" style={{ height: '70vh' }}>
            <DataTable
                idAccessor={'employee_id'}
                pinFirstColumn
                pinLastColumn
                withColumnBorders
                fetching={loadingTable}
                noRecordsText="Chưa có dữ liệu"
                highlightOnHover
                className="whitespace-nowrap table-hover"
                records={dataTable}
                columns={columnEffect}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                page={page}
                onPageChange={(page) => setPage(page)}
                recordsPerPageOptions={PAGE_SIZES}
                onRecordsPerPageChange={setLimit}
                minHeight={200}
                paginationText={({ from, to, totalRecords }) => `Hiển thị ${from} đến ${to} của ${totalRecords} hàng`}
            />
        </div>
    )
}

export default AttendanceManagementTable